import { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import Home from "./components/Home/Home";
import GrowAudience from "./components/GrowAudience/GrowAudience";
import Automation from "./components/Automation/Automation";
import Segmentation from "./components/Segmentation/Segmentation";
import Company from "./components/Company/Company";
import Pricing from "./components/Pricing/Pricing";
import About from "./components/About/About";
import Analytics from "./components/Analytics/Analytics";
import Features from "./components/Features/Features";
import Guide from "./components/Guide/Guide";
import GuideGettingStarted from "./components/GuideGettingStarted/GuideGettingStarted";
import GuideSubjectLines from "./components/GuideSubjectLines/GuideSubjectLines";
import GuideEffectiveEmail from "./components/GuideEffectiveEmail/GuideEffectiveEmail";
import GuidePersonalising from "./components/GuidePersonalising/GuidePersonalising";
import GuideScheduling from "./components/GuideScheduling/GuideScheduling";
import GuideMeasureSuccess from "./components/GuideMeasureSuccess/GuideMeasureSuccess";
import TermsOfUse from "./components/Legal/TermsOfUse";
import AcceptableUsePolicy from "./components/Legal/AcceptableUsePolicy";
import PrivacyPolicy from "./components/Legal/PrivacyPolicy";
import CookiePolicy from "./components/Legal/CookiePolicy";
import GDPR from "./components/Legal/GDPR";
import Slider from "./components/Slider";
import Templates from "./components/Templates/Templates";
import TemplateDetail from "./components/Templates/TemplateDetail";
import BlogList from "./components/Blog/BlogList";
import BlogDetail from "./components/Blog/BlogDetail";
import ToolPage from "./components/ToolPage/ToolPage";
import AiTemplateDesigner from "./components/AiTemplateDesigner/AiTemplateDesigner";
import SEO from "./components/SEO/SEO";
import Breadcrumbs from "./components/Breadcrumbs/Breadcrumbs";
import SeoLandingPage from "./components/SeoLandingPage/SeoLandingPage";
import CompetitorAlternative from "./components/CompetitorAlternative/CompetitorAlternative";
import Alternatives from "./components/Alternatives/Alternatives";
import FAQ from "./components/FAQ/FAQ";
import Contact from "./components/Contact/Contact";
import NotFound from "./components/NotFound/NotFound";
import { applySeo, canonicalPath, metaForPath } from "./seo/seo";

function RouteEffects({ seoOverrides = {} }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let observer;
    const normalizedPath = canonicalPath(pathname);

    if (normalizedPath !== pathname) {
      navigate(normalizedPath, { replace: true });
      return undefined;
    }

    applySeo(seoOverrides[normalizedPath] || metaForPath(normalizedPath));
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    const frame = window.requestAnimationFrame(() => {
      const sections = Array.from(document.querySelectorAll("main section"));

      sections.forEach((section, index) => {
        section.classList.add("section-reveal");
        section.classList.add("section-reveal--visible");
        section.style.setProperty("--section-delay", `${Math.min(index * 0.06, 0.24)}s`);
      });
    });

    return () => {
      window.cancelAnimationFrame(frame);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [navigate, pathname, seoOverrides]);

  return null;
}

function App({ RouterComponent = Router, routerProps = {}, prerenderData = {}, seoOverrides = {} }) {
  return (
    <RouterComponent {...routerProps}>
      <RouteEffects seoOverrides={seoOverrides} />
      <SEO metaByPath={seoOverrides} />
      <Header />
      <main>
        <Breadcrumbs metaByPath={seoOverrides} />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/features" element={<Features />} />
          <Route path="/grow-audience" element={<GrowAudience />} />
          <Route path="/automation" element={<Automation />} />
          <Route path="/segmentation" element={<Segmentation />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/about" element={<About />} />
          <Route path="/company" element={<Company />} />
          <Route path="/guide" element={<Guide />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/templates" element={<Templates initialData={prerenderData["/templates"]} />} />
          <Route path="/templates/category/:categorySlug" element={<Templates initialData={prerenderData[canonicalPath(typeof window === "undefined" ? routerProps.location : window.location.pathname)]} />} />
          <Route path="/templates/category/:categorySlug/:subcategorySlug" element={<Templates initialData={prerenderData[canonicalPath(typeof window === "undefined" ? routerProps.location : window.location.pathname)]} />} />
          <Route path="/templates/:templateId" element={<TemplateDetail initialData={prerenderData[canonicalPath(typeof window === "undefined" ? routerProps.location : window.location.pathname)]} />} />
          <Route path="/ai-template-designer" element={<AiTemplateDesigner />} />
          <Route path="/mailchimp-alternative" element={<CompetitorAlternative competitor="Mailchimp" />} />
          <Route path="/alternatives" element={<Alternatives />} />
          <Route path="/brevo-alternative" element={<CompetitorAlternative competitor="Brevo" />} />
          <Route path="/constant-contact-alternative" element={<CompetitorAlternative competitor="Constant Contact" />} />
          <Route path="/mailerlite-alternative" element={<CompetitorAlternative competitor="MailerLite" />} />
          <Route path="/klaviyo-alternative" element={<CompetitorAlternative competitor="Klaviyo" />} />
          <Route path="/email-builder" element={<ToolPage type="builder" />} />
          <Route path="/audience-tools" element={<ToolPage type="audience" />} />
          <Route path="/automation-builder" element={<ToolPage type="automation" />} />
          <Route path="/segmentation-tools" element={<ToolPage type="segmentation" />} />
          <Route path="/email-marketing-software" element={<SeoLandingPage type="software" />} />
          <Route path="/email-marketing-platform" element={<SeoLandingPage type="platform" />} />
          <Route path="/email-automation-software" element={<SeoLandingPage type="automation" />} />
          <Route path="/newsletter-software" element={<SeoLandingPage type="newsletter" />} />
          <Route path="/email-campaign-software" element={<SeoLandingPage type="campaign" />} />
          <Route path="/email-template-builder" element={<SeoLandingPage type="templateBuilder" />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<BlogList initialData={prerenderData["/blog"]} />} />
          <Route path="/blog/category/:categorySlug" element={<BlogList initialData={prerenderData[canonicalPath(typeof window === "undefined" ? routerProps.location : window.location.pathname)]} />} />
          <Route path="/blog/:slug" element={<BlogDetail initialData={prerenderData[canonicalPath(typeof window === "undefined" ? routerProps.location : window.location.pathname)]} />} />
          <Route path="/gettingstarted" element={<GuideGettingStarted />} />
          <Route path="/GuideSubjectLines" element={<GuideSubjectLines />} />
          <Route path="/GuideEffectiveEmail" element={<GuideEffectiveEmail />} />
          <Route path="/GuidePersonalising" element={<GuidePersonalising />} />
          <Route path="/GuideScheduling" element={<GuideScheduling />} />
          <Route path="/GuideMeasureSuccess" element={<GuideMeasureSuccess />} />
          <Route path="/terms" element={<TermsOfUse />} />
          <Route path="/acceptable" element={<AcceptableUsePolicy />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookie" element={<CookiePolicy />} />
          <Route path="/gdpr" element={<GDPR />} />
          <Route path="/slider" element={<Slider />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </RouterComponent>
  );
}

export default App;
